import html2pdf from "html2pdf.js";

export type DocumentType = "terms" | "privacy" | "disclaimer";

const documentTitles: Record<DocumentType, string> = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  disclaimer: "Disclaimer",
};

export const generatePDF = (
  documentType: DocumentType,
  htmlContent: HTMLElement
) => {
  const title = documentTitles[documentType];
  const filename = `Blockseer-${title.replace(/\s+/g, "-")}-${new Date().getFullYear()}.pdf`;

  const element = document.createElement("div");
  element.style.padding = "20px";
  element.style.fontFamily = "Arial, sans-serif";
  element.style.lineHeight = "1.6";
  element.style.color = "#333";

  // Add header with title and date
  const header = document.createElement("div");
  header.style.marginBottom = "20px";
  header.style.borderBottom = "2px solid #333";
  header.style.paddingBottom = "10px";

  const titleEl = document.createElement("h1");
  titleEl.textContent = title;
  titleEl.style.margin = "0 0 10px 0";
  titleEl.style.fontSize = "24px";
  titleEl.style.fontWeight = "bold";

  const dateEl = document.createElement("p");
  dateEl.textContent = `Generated on: ${new Date().toLocaleDateString()}`;
  dateEl.style.margin = "0";
  dateEl.style.fontSize = "12px";
  dateEl.style.color = "#666";

  header.appendChild(titleEl);
  header.appendChild(dateEl);
  element.appendChild(header);

  // Add the content (clone to avoid modifying original)
  const contentClone = htmlContent.cloneNode(true) as HTMLElement;
  // Remove dark mode specific styles
  contentClone.style.color = "#333";
  contentClone.style.backgroundColor = "white";
  element.appendChild(contentClone);

  // PDF options
  const options = {
    margin: 10,
    filename: filename,
    image: { type: "png" as const, quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { orientation: "portrait" as const, unit: "mm" as const, format: "a4" },
  };

  html2pdf().set(options).from(element).save();
};

export const printDocument = (documentType: DocumentType) => {
  const title = documentTitles[documentType];

  // Create a new window for printing
  const printWindow = window.open("", "", "width=800,height=600");
  if (!printWindow) {
    alert("Please allow popups for printing");
    return;
  }

  const contentElement = document.querySelector(
    `[data-document-type="${documentType}"]`
  );
  if (!contentElement) return;

  // Clone the content and remove Tailwind classes from header divs
  const contentClone = contentElement.cloneNode(true) as HTMLElement;
  contentClone.querySelectorAll('div[class*="text-"]').forEach(el => {
    const div = el as HTMLElement;
    // Only process divs that are section headers (contain Tailwind text classes)
    if (div.className.includes('font-bold')) {
      div.setAttribute('data-header', 'true'); // Mark as header for CSS targeting
      div.className = ''; // Remove all Tailwind classes
      div.style.cssText = ''; // Remove any inline styles
    }
  });

  const content = contentClone.innerHTML;

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            padding: 20px;
            max-width: 8.5in;
            margin: 0 auto;
          }
          h1 {
            font-size: 24px;
            margin-bottom: 10px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          h3 {
            font-size: 22px !important;
            margin-top: 20px !important;
            margin-bottom: 16px !important;
            font-weight: bold !important;
          }
          /* Style for section headers (marked with data-header attribute) */
          div[data-header="true"] {
            font-size: 22px;
            font-weight: bold;
            margin-top: 20px;
            margin-bottom: 16px;
          }
          p {
            margin: 10px 0;
          }
          ul, ol {
            margin: 10px 0;
            padding-left: 30px;
          }
          li {
            margin: 5px 0;
          }
          .print-date {
            font-size: 12px;
            color: #666;
            margin-bottom: 20px;
          }
          @media print {
            body {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div class="print-date">Generated on: ${new Date().toLocaleDateString()}</div>
        <div>${content}</div>
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();

  // Give it a moment to render before printing
  setTimeout(() => {
    printWindow.print();
  }, 250);
};
